/**
 * forgedops-readability-shim.js
 *
 * Late-bound style injection. The bundled CSS competes with Tailwind's
 * @layer utilities and (in this environment) loses on cascade order.
 * Injecting the same readability rules via a runtime <style> tag puts
 * them at the absolute end of the document so they always win.
 *
 * This file MUST be imported AFTER `@/index.css` in `src/index.js`.
 */
const RULES = `
/* Form-page contrast — Tailwind text-slate-* on the dark ForgedOps page */
html body .text-slate-900,
html body .text-gray-900,
html body .text-neutral-900,
html body .text-zinc-900,
html body .text-stone-900,
html body .text-black { color: #FFFFFF !important; }
html body .text-slate-800, html body .text-gray-800, html body .text-neutral-800,
html body .text-slate-700, html body .text-gray-700, html body .text-neutral-700 { color: #FFFFFF !important; }
html body .text-slate-600, html body .text-gray-600, html body .text-neutral-600,
html body .text-slate-500, html body .text-gray-500, html body .text-neutral-500,
html body .text-slate-400, html body .text-gray-400, html body .text-neutral-400,
html body .text-slate-300, html body .text-gray-300 { color: #BDBDBD !important; }

/* Restore native dark text inside light alert boxes (amber-50/red-50/etc) */
html body .bg-amber-50  .text-slate-900, html body .bg-amber-50  .text-gray-900, html body .bg-amber-50  .text-black,
html body .bg-yellow-50 .text-slate-900, html body .bg-orange-50 .text-slate-900,
html body .bg-red-50    .text-slate-900, html body .bg-red-50    .text-gray-900,
html body .bg-rose-50   .text-slate-900, html body .bg-pink-50   .text-slate-900,
html body .bg-green-50  .text-slate-900, html body .bg-green-50  .text-gray-900,
html body .bg-emerald-50 .text-slate-900, html body .bg-teal-50 .text-slate-900,
html body .bg-cyan-50   .text-slate-900,
html body .bg-blue-50   .text-slate-900, html body .bg-blue-50   .text-gray-900,
html body .bg-sky-50    .text-slate-900, html body .bg-indigo-50 .text-slate-900,
html body .bg-violet-50 .text-slate-900, html body .bg-purple-50 .text-slate-900,
html body .bg-amber-100 .text-slate-900, html body .bg-yellow-100 .text-slate-900,
html body .bg-red-100   .text-slate-900, html body .bg-orange-100 .text-slate-900
{ color: rgb(15 23 42) !important; }

html body .bg-amber-50  .text-slate-800, html body .bg-amber-50  .text-gray-800,
html body .bg-yellow-50 .text-slate-800, html body .bg-orange-50 .text-slate-800,
html body .bg-red-50    .text-slate-800, html body .bg-rose-50   .text-slate-800,
html body .bg-green-50  .text-slate-800, html body .bg-emerald-50 .text-slate-800,
html body .bg-blue-50   .text-slate-800, html body .bg-sky-50    .text-slate-800,
html body .bg-indigo-50 .text-slate-800,
html body .bg-amber-100 .text-slate-800, html body .bg-red-100   .text-slate-800
{ color: rgb(30 41 59) !important; }

html body .bg-amber-50  .text-slate-700, html body .bg-amber-50  .text-gray-700,
html body .bg-yellow-50 .text-slate-700, html body .bg-orange-50 .text-slate-700,
html body .bg-red-50    .text-slate-700, html body .bg-rose-50   .text-slate-700,
html body .bg-green-50  .text-slate-700, html body .bg-emerald-50 .text-slate-700,
html body .bg-blue-50   .text-slate-700, html body .bg-sky-50    .text-slate-700,
html body .bg-indigo-50 .text-slate-700,
html body .bg-amber-100 .text-slate-700, html body .bg-red-100   .text-slate-700
{ color: rgb(51 65 85) !important; }

html body .bg-amber-50  .text-slate-600, html body .bg-yellow-50 .text-slate-600,
html body .bg-orange-50 .text-slate-600, html body .bg-red-50    .text-slate-600,
html body .bg-rose-50   .text-slate-600, html body .bg-green-50  .text-slate-600,
html body .bg-emerald-50 .text-slate-600,
html body .bg-blue-50   .text-slate-600, html body .bg-sky-50    .text-slate-600,
html body .bg-amber-100 .text-slate-600, html body .bg-red-100   .text-slate-600
{ color: rgb(71 85 105) !important; }

html body .bg-amber-50  .text-slate-500, html body .bg-yellow-50 .text-slate-500,
html body .bg-orange-50 .text-slate-500, html body .bg-red-50    .text-slate-500,
html body .bg-rose-50   .text-slate-500, html body .bg-green-50  .text-slate-500,
html body .bg-emerald-50 .text-slate-500,
html body .bg-blue-50   .text-slate-500, html body .bg-sky-50    .text-slate-500
{ color: rgb(100 116 139) !important; }

/* Severity tint headlines inside light alerts */
html body .bg-amber-50  .text-amber-900, html body .bg-amber-100 .text-amber-900 { color: rgb(120 53 15)  !important; }
html body .bg-amber-50  .text-amber-800, html body .bg-amber-100 .text-amber-800 { color: rgb(146 64 14)  !important; }
html body .bg-amber-50  .text-amber-700, html body .bg-amber-100 .text-amber-700 { color: rgb(180 83 9)   !important; }
html body .bg-yellow-50 .text-yellow-800 { color: rgb(133 77 14)  !important; }
html body .bg-yellow-50 .text-yellow-700 { color: rgb(161 98 7)   !important; }
html body .bg-orange-50 .text-orange-800 { color: rgb(154 52 18)  !important; }
html body .bg-orange-50 .text-orange-700 { color: rgb(194 65 12)  !important; }
html body .bg-red-50    .text-red-900,   html body .bg-red-100   .text-red-900   { color: rgb(127 29 29) !important; }
html body .bg-red-50    .text-red-800,   html body .bg-red-100   .text-red-800   { color: rgb(153 27 27) !important; }
html body .bg-red-50    .text-red-700,   html body .bg-red-100   .text-red-700   { color: rgb(185 28 28) !important; }
html body .bg-rose-50   .text-rose-700  { color: rgb(190 18 60)  !important; }
html body .bg-green-50  .text-green-900, html body .bg-green-100 .text-green-900 { color: rgb(20 83 45)  !important; }
html body .bg-green-50  .text-green-800, html body .bg-green-100 .text-green-800 { color: rgb(22 101 52) !important; }
html body .bg-green-50  .text-green-700, html body .bg-green-100 .text-green-700 { color: rgb(21 128 61) !important; }
html body .bg-emerald-50 .text-emerald-700 { color: rgb(4 120 87) !important; }
html body .bg-blue-50   .text-blue-900   { color: rgb(30 58 138) !important; }
html body .bg-blue-50   .text-blue-800   { color: rgb(30 64 175) !important; }
html body .bg-blue-50   .text-blue-700   { color: rgb(29 78 216) !important; }
html body .bg-sky-50    .text-sky-700    { color: rgb(3 105 161) !important; }
html body .bg-indigo-50 .text-indigo-700 { color: rgb(67 56 202) !important; }

/* Inputs: dark chrome → white text + readable placeholder */
html body input[type="text"], html body input[type="number"], html body input[type="email"],
html body input[type="time"], html body input[type="date"], html body input[type="search"],
html body input[type="tel"], html body input[type="url"], html body input[type="password"],
html body select, html body textarea { color: #FFFFFF !important; }
html body input::placeholder, html body textarea::placeholder { color: #8a8a8a !important; opacity: 1 !important; }

/* Inputs inside a light card (alert): dark text + slate-500 placeholder */
html body .bg-amber-50 input, html body .bg-amber-50 textarea,
html body .bg-red-50 input, html body .bg-red-50 textarea,
html body .bg-green-50 input, html body .bg-green-50 textarea,
html body .bg-blue-50 input, html body .bg-blue-50 textarea
{ color: rgb(15 23 42) !important; }
html body .bg-amber-50 input::placeholder, html body .bg-amber-50 textarea::placeholder,
html body .bg-red-50 input::placeholder, html body .bg-red-50 textarea::placeholder,
html body .bg-green-50 input::placeholder, html body .bg-green-50 textarea::placeholder
{ color: rgb(100 116 139) !important; opacity: 1 !important; }

/* Sonner toasts */
html body [data-sonner-toast][data-styled="true"] {
  background: #1A1A1A !important;
  color: #FFFFFF !important;
  border: 1px solid #2A2A2A !important;
}
html body [data-sonner-toast][data-styled="true"][data-type="success"] { border-left: 3px solid #2E7D32 !important; }
html body [data-sonner-toast][data-styled="true"][data-type="error"]   { border-left: 3px solid #D32F2F !important; }
html body [data-sonner-toast][data-styled="true"][data-type="warning"] { border-left: 3px solid #FFD000 !important; }
html body [data-sonner-toast] [data-title]       { color: #FFFFFF !important; font-weight: 700 !important; }
html body [data-sonner-toast] [data-description] { color: #BDBDBD !important; }
`;

function inject() {
  if (typeof document === "undefined") return;
  if (document.getElementById("forgedops-readability-shim")) return;
  const style = document.createElement("style");
  style.id = "forgedops-readability-shim";
  style.setAttribute("data-fo-shim", "readability");
  style.textContent = RULES;
  // Append to <html> not <head> so it loads at the very end of the cascade
  document.head.appendChild(style);
}

inject();

// Re-inject if something nukes the tag (HMR, third-party widgets, etc.)
if (typeof MutationObserver !== "undefined") {
  const obs = new MutationObserver(() => {
    if (!document.getElementById("forgedops-readability-shim")) inject();
  });
  obs.observe(document.head, { childList: true });
}

export default RULES;
